import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useSWR from "swr";
import {
  Table, Modal, Form, Input, InputNumber, Select, message,
  Popconfirm, Button, Space, Tag, Descriptions,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  getIndicatorDetail,
  getDiagnosisList, createDiagnosis, updateDiagnosis, deleteDiagnosis, toggleDiagnosis,
  getSolutionGroupSelect,
} from "../api/domainApi";
import { DiagnosisRes } from "../types/domain";
import PageHeader from "../components/common/PageHeader";
import UseYnTag from "../components/common/UseYnTag";

const StageLevelMap: Record<number, string> = { 1: "안정/낮음", 2: "주의/보통", 3: "위험/높음" };

const IndicatorDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const indicatorId = Number(id);

  const [diagModalOpen, setDiagModalOpen] = useState(false);
  const [editingDiag, setEditingDiag] = useState<DiagnosisRes | null>(null);
  const [diagForm] = Form.useForm();

  const { data: indicator } = useSWR(
    indicatorId ? `/indicator/${indicatorId}/detail` : null,
    () => getIndicatorDetail(indicatorId)
  );

  const { data: diagData, mutate: mutateDiag } = useSWR(
    indicatorId ? `/indicator/${indicatorId}/diagnosis` : null,
    () => getDiagnosisList(indicatorId, { page: 0, size: 100 })
  );

  const { data: sltnGroups } = useSWR("/solution-group/select", getSolutionGroupSelect);

  const openDiagCreate = () => { setEditingDiag(null); diagForm.resetFields(); setDiagModalOpen(true); };
  const openDiagEdit = (row: DiagnosisRes) => { setEditingDiag(row); diagForm.setFieldsValue(row); setDiagModalOpen(true); };

  const handleDiagOk = async () => {
    const values = await diagForm.validateFields();
    try {
      if (editingDiag) {
        await updateDiagnosis(indicatorId, editingDiag.id, values);
        message.success("수정되었습니다.");
      } else {
        await createDiagnosis(indicatorId, values);
        message.success("추가되었습니다.");
      }
      setDiagModalOpen(false);
      mutateDiag();
    } catch (e: any) { message.error(e.message); }
  };

  const handleDiagDelete = async (diagId: number) => {
    try {
      await deleteDiagnosis(indicatorId, diagId);
      message.success("삭제되었습니다.");
      mutateDiag();
    } catch (e: any) { message.error(e.message); }
  };

  const diagColumns: ColumnsType<DiagnosisRes> = [
    {
      title: "단계", dataIndex: "stageLevel", width: 80,
      render: (v) => <Tag color={v === 1 ? "green" : v === 2 ? "orange" : "red"}>{StageLevelMap[v]}</Tag>,
    },
    { title: "단계명", dataIndex: "stageName", width: 100 },
    { title: "솔루션그룹", dataIndex: "sltnGrpTitle", width: 140 },
    { title: "점수범위", width: 110, render: (_, r) => `${r.minScore} ~ ${r.maxScore}` },
    { title: "설명", dataIndex: "description", ellipsis: { showTitle: true } },
    { title: "사용여부", dataIndex: "useYn", width: 80, render: (v) => <UseYnTag value={v} /> },
    {
      title: "관리", width: 160,
      render: (_, row) => (
        <Space>
          <Button size="small" onClick={() => openDiagEdit(row)}>수정</Button>
          <Button size="small" onClick={async () => {
            try { await toggleDiagnosis(indicatorId, row.id); mutateDiag(); }
            catch (e: any) { message.error(e.message); }
          }}>{row.useYn ? "비활성" : "활성"}</Button>
          <Popconfirm title="삭제?" onConfirm={() => handleDiagDelete(row.id)}>
            <Button size="small" danger>삭제</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <PageHeader title={`인디케이터 상세 — ${indicator?.nameKo ?? `#${indicatorId}`}`} />
      <Button style={{ marginBottom: 16 }} onClick={() => navigate("/indicators")}>← 목록</Button>

      {indicator && (
        <Descriptions bordered size="small" style={{ marginBottom: 24 }} column={3}>
          <Descriptions.Item label="코드">{indicator.code}</Descriptions.Item>
          <Descriptions.Item label="그룹">{indicator.grpName}</Descriptions.Item>
          <Descriptions.Item label="축">{indicator.axisType}</Descriptions.Item>
          <Descriptions.Item label="한글명">{indicator.nameKo}</Descriptions.Item>
          <Descriptions.Item label="영문명">{indicator.nameEn}</Descriptions.Item>
          <Descriptions.Item label="순서">{indicator.sortOrder}</Descriptions.Item>
          <Descriptions.Item label="사용여부"><UseYnTag value={indicator.useYn} /></Descriptions.Item>
        </Descriptions>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <strong>진단 목록</strong>
        <Button type="primary" onClick={openDiagCreate}>진단 추가</Button>
      </div>
      <Table
        rowKey="id" columns={diagColumns} dataSource={diagData?.content}
        pagination={false} size="small"
      />

      <Modal
        title={editingDiag ? "진단 수정" : "진단 추가"}
        open={diagModalOpen} onOk={handleDiagOk} onCancel={() => setDiagModalOpen(false)}
        okText={editingDiag ? "수정" : "추가"} cancelText="취소"
      >
        <Form form={diagForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="sltnGrpId" label="솔루션 그룹" rules={[{ required: true }]}>
            <Select options={sltnGroups?.map((g) => ({ value: g.id, label: g.sltnGrpTitle }))} />
          </Form.Item>
          <Form.Item name="stageLevel" label="단계 레벨" rules={[{ required: true }]}>
            <Select options={[
              { value: 1, label: "1 - 안정/낮음" },
              { value: 2, label: "2 - 주의/보통" },
              { value: 3, label: "3 - 위험/높음" },
            ]} />
          </Form.Item>
          <Form.Item name="stageName" label="단계명" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="description" label="설명" rules={[{ required: true }]}>
            <Input.TextArea rows={3} maxLength={500} showCount />
          </Form.Item>
          <Space>
            <Form.Item name="minScore" label="최소 점수" rules={[{ required: true }]}>
              <InputNumber />
            </Form.Item>
            <Form.Item name="maxScore" label="최대 점수" rules={[{ required: true }]}>
              <InputNumber />
            </Form.Item>
          </Space>
        </Form>
      </Modal>
    </div>
  );
};

export default IndicatorDetailPage;
